/**
 * Verification Quick Action
 *
 * Triggered from the notification panel when a user has unfinished identity
 * verification. Opens a thin "go to KYC" prompt — there is no embedded
 * verification flow, since the platform now uses manual KYC handled on a
 * dedicated dashboard page.
 *
 * Same props (`isOpen`, `onClose`, `onSuccess`) as the legacy version so
 * NotificationPanel doesn't need to change.
 *
 * @module features/user-dashboard/components/verification/VerificationQuickAction
 */

import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, ArrowRight, X } from 'lucide-react';
import { useLocaleNavigate } from '@lib/i18n/exports';

interface VerificationQuickActionProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const VerificationQuickAction: React.FC<VerificationQuickActionProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const navigate = useLocaleNavigate();

  if (!isOpen) return null;

  const handleOpenKyc = (): void => {
    navigate('/dashboard/kyc');
    onSuccess();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative mx-4 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>

          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-emerald-500 to-teal-600">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Verify your identity
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Complete identity verification on your dashboard. It takes a few
                minutes and unlocks campaign creation.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleOpenKyc}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white shadow-xs transition-colors hover:bg-emerald-700"
              >
                <span>Open KYC</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default VerificationQuickAction;
